/**NIRSALFinancials — Credit enablement institution view */
import React from "react";
import { useNIRSAL } from "@/context/NIRSALContext";
import { PageHeader, SectionCard } from "@/components/ds";

const fmtU = (v) => { if (!v) return "—"; const n=Number(v); if (n>=1e6) return `$${(n/1e6).toFixed(1)}M`; if (n>=1000) return `$${(n/1000).toFixed(0)}K`; return `$${n}`; };
const fmtN = (v, u="") => { if (!v&&v!==0) return "—"; const n=Number(v); if (n>=1e6) return `${(n/1e6).toFixed(1)}M ${u}`.trim(); if (n>=1000) return `${(n/1000).toFixed(0)}K ${u}`.trim(); return `${n} ${u}`.trim(); };

export default function NIRSALFinancials() {
  const { consolidated, vbCache, selectedPeriod, loading } = useNIRSAL();
  const p  = consolidated?.portfolio || {};
  const vb = vbCache[selectedPeriod] || {};
  const opps = vb.opportunities || consolidated?.opportunities || [];

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading financials…</div>;

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-6">
      <PageHeader label="FINANCIALS" title="" />
      <p className="text-xs text-slate-400 -mt-4">NIRSAL as credit-enablement institution · CRG leverage · Capital mobilization · Indicative carbon/plastic value separated</p>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-700">
        <strong>Mandatory disclosure:</strong> Carbon and plastic values are indicative and conditional on VVB validation and registry issuance. They must NOT be included in base DSCR or CRG calculations until formally issued.
      </div>

      {/* Section A: Operational/Financial Value */}
      <SectionCard title="Section A — Green Credit Portfolio (CRG-backed)">
        <div className="p-4 grid grid-cols-3 gap-4">
          {[
            ["Capital Mobilized", fmtU(p.total_capital_mobilized), "Total green finance deployed"],
            ["CRG Exposure", fmtU(p.total_crg_exposure), "Guarantee coverage issued"],
            ["Blended Leverage", (p.leverage_ratio||0).toFixed(1) + "x", "Capital / CRG ratio"],
            ["Active Clusters", p.active_clusters || 0, "Fully operational"],
            ["Financed Clusters", p.financed_clusters || 0, "Active + recently financed"],
            ["States Covered", p.states_count || 0, "Geographic reach"],
          ].map(([k,v,s]) => (
            <div key={k} className="bg-white rounded-lg border border-slate-100 p-4">
              <p className="text-xs text-slate-400">{k}</p>
              <p className="text-xl font-black text-[#1e3a5f]">{v}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">{s}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Financing products */}
      <SectionCard title="Financing Product Pipeline">
        <div className="overflow-x-auto p-2">
          <table className="w-full text-xs">
            <thead><tr className="border-b border-slate-100">
              {["Cluster","State","Product","Capital Mob.","CRG %","PFI","Status"].map(h => <th key={h} className="text-left py-2 px-3 text-slate-400 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {opps.map((o, i) => {
                const c = consolidated?.clusters?.find(x => x.cluster_id === o.opp_id) || {};
                return (
                  <tr key={o.opp_id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2 px-3 font-medium max-w-[150px] truncate">{o.name}</td>
                    <td className="py-2 px-3 text-slate-500">{o.state}</td>
                    <td className="py-2 px-3 text-slate-500">{c.financing_product || "—"}</td>
                    <td className="py-2 px-3 font-bold">{fmtU(o.capital_mobilized)}</td>
                    <td className="py-2 px-3">{(o.crg_coverage_pct||0).toFixed(0)}%</td>
                    <td className="py-2 px-3 text-slate-500">{o.pfi_name || "—"}</td>
                    <td className="py-2 px-3 capitalize"><span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${o.status==="active" ? "bg-emerald-100 text-emerald-700" : o.status==="financed" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>{o.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Section B: Indicative Carbon Value */}
      <div className="grid grid-cols-2 gap-4">
        <SectionCard title="Section B — Indicative Carbon Value (Conditional)">
          <div className="p-4 space-y-2">
            {[
              ["Low ($10/t)", fmtU(p.total_reduction_tco2e_yr * 10) + "/yr"],
              ["Base ($20/t)", fmtU(p.carbon_value_base_usd) + "/yr"],
              ["High ($40/t)", fmtU(p.carbon_value_high_usd) + "/yr"],
              ["Total reduction", fmtN(p.total_reduction_tco2e_yr, "tCO₂e/yr")],
            ].map(([k,v]) => (
              <div key={k} className="flex justify-between text-xs py-1.5 border-b border-slate-50">
                <span className="text-slate-500">{k}</span><span className="font-bold text-emerald-700">{v}</span>
              </div>
            ))}
            <p className="text-[10px] text-red-500 pt-2">Must NOT be included in CRG or DSCR calculations until VVB validated and issued</p>
          </div>
        </SectionCard>
        <SectionCard title="Section C — Indicative Plastic Value (Conditional)">
          <div className="p-4 space-y-2">
            {[
              ["Plastic recovered", fmtN(p.total_plastic_recovered_t_yr, "t/yr")],
              ["Plastic value ($150/t)", fmtU(p.plastic_value_usd) + "/yr"],
              ["Standard", "Verra Plastic Waste Reduction"],
              ["Clusters involved", (p.plastic_clusters||0) + " clusters"],
            ].map(([k,v]) => (
              <div key={k} className="flex justify-between text-xs py-1.5 border-b border-slate-50">
                <span className="text-slate-500">{k}</span><span className="font-bold text-purple-700">{v}</span>
              </div>
            ))}
            <p className="text-[10px] text-red-500 pt-2">Conditional on plastic credit registry verification and issuance</p>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
